import React, { useEffect, useState } from "react";
import { listHelpArticles } from "../lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { SkeletonCard } from "../components/shared/Skeleton";
import ErrorBanner from "../components/shared/ErrorBanner";
import { Mail, Phone } from "lucide-react";
import { t } from "../lib/i18n";

export default function Help() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await listHelpArticles();
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  return (
    <section
      data-testid="help-page"
      className="mx-auto max-w-3xl px-5 pt-20 pb-24 sm:px-6 sm:pt-28"
    >
      <p className="text-xs uppercase tracking-[0.32em] text-[#C8A24B]">
        {t("brand.name")}
      </p>
      <h1 className="mt-3 font-display text-4xl text-[#1A1A1A] sm:text-5xl">
        {t("help.title")}
      </h1>
      <p className="mt-4 text-base text-[#1A1A1A]/65">{t("help.subtitle")}</p>

      <div className="mt-12">
        {loading ? (
          <ul className="space-y-4">
            {[0, 1, 2].map((i) => (
              <li key={i}>
                <SkeletonCard />
              </li>
            ))}
          </ul>
        ) : error ? (
          <ErrorBanner onRetry={() => setRetryKey((k) => k + 1)} />
        ) : (
          <Accordion type="single" collapsible>
            {items.map((a) => (
              <AccordionItem
                key={a.id}
                value={a.id}
                data-testid={`help-item-${a.slug}`}
                className="border-[#EAE3D2]"
              >
                <AccordionTrigger className="text-left text-base text-[#1A1A1A]">
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.24em] text-[#1A1A1A]/45">
                      {a.category}
                    </span>
                    {a.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-[#1A1A1A]/75">
                  {a.body_markdown || a.body || ""}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>

      <div
        data-testid="help-contact-card"
        className="mt-16 rounded-lg border border-[#EAE3D2] bg-white p-7 sm:p-10"
      >
        <h2 className="font-display text-2xl text-[#1A1A1A] sm:text-3xl">
          {t("help.contact.title")}
        </h2>
        <p className="mt-2 text-sm text-[#1A1A1A]/65 sm:text-base">
          {t("help.contact.subtitle")}
        </p>
        <ul className="mt-6 space-y-3 text-sm text-[#1A1A1A]/85">
          <li className="flex items-center gap-3">
            <Mail className="h-4 w-4 text-[#C8A24B]" />
            <a
              data-testid="help-contact-email"
              href={`mailto:${t("help.contact.email")}`}
              className="transition hover:text-[#C8A24B]"
            >
              {t("help.contact.email")}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <Phone className="h-4 w-4 text-[#C8A24B]" />
            <a
              data-testid="help-contact-phone"
              href={`tel:${t("help.contact.phone").replace(/\s/g, "")}`}
              className="transition hover:text-[#C8A24B]"
            >
              {t("help.contact.phone")}
            </a>
          </li>
        </ul>
      </div>
    </section>
  );
}
